import { lazy, Suspense, Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type Shipment, STATUS_CONFIG } from '../../../mocks/shipments';

const ShipmentMap = lazy(() => import('../../../components/feature/ShipmentMap'));

// ── Map error boundary ────────────────────────────────────────────────────
interface EBState { hasError: boolean }
class MapErrorBoundary extends Component<{ children: ReactNode; origin: string; dest: string }, EBState> {
  constructor(props: MapErrorBoundary['props']) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): EBState { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-[#071818]">
          <i className="ri-earth-line text-4xl text-teal-800 mb-3" />
          <p className="text-teal-600 text-xs text-center">Map unavailable</p>
          <p className="text-teal-800 text-xs mt-1">{this.props.origin} → {this.props.dest}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

interface Props {
  shipment: Shipment | null;
  onClose: () => void;
  onEdit: (s: Shipment) => void;
  onStatusUpdate: (s: Shipment) => void;
}

export default function AdminShipmentDetailPanel({ shipment, onClose, onEdit, onStatusUpdate }: Props) {
  const isOpen = shipment !== null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(0,0,0,0.55)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 w-full max-w-xl bg-[#0d2020] border-l border-teal-900/40 flex flex-col transition-transform duration-300 ease-out overflow-hidden`}
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {shipment && <PanelContent shipment={shipment} onClose={onClose} onEdit={onEdit} onStatusUpdate={onStatusUpdate} />}
      </div>
    </>
  );
}

function PanelContent({ shipment, onClose, onEdit, onStatusUpdate }: { shipment: Shipment } & Omit<Props, 'shipment'>) {
  const cfg = STATUS_CONFIG[shipment.status];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div>
            <div className="font-mono font-bold text-white text-sm tracking-wider">{shipment.id}</div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border mt-1 ${cfg.bg} ${cfg.color}`}>
              <i className={`${cfg.icon} text-xs`} />{cfg.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/track/${shipment.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-900/30 hover:bg-teal-800/40 text-teal-400 hover:text-teal-300 transition-colors cursor-pointer"
            title="Open tracking page"
          >
            <i className="ri-external-link-line text-sm" />
          </Link>
          <button
            onClick={() => onStatusUpdate(shipment)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-900/30 hover:bg-teal-800/40 text-teal-400 hover:text-teal-300 transition-colors cursor-pointer"
            title="Update status"
          >
            <i className="ri-refresh-line text-sm" />
          </button>
          <button
            onClick={() => onEdit(shipment)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-900/30 hover:bg-teal-800/40 text-teal-400 hover:text-sky-300 transition-colors cursor-pointer"
            title="Edit shipment"
          >
            <i className="ri-file-edit-line text-sm" />
          </button>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-600 hover:text-white hover:bg-white/10 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Map */}
        <div className="relative" style={{ height: '220px' }}>
          <MapErrorBoundary origin={shipment.origin?.city || '?'} dest={shipment.destination?.city || '?'}>
            <Suspense fallback={
              <div className="w-full h-full flex items-center justify-center bg-[#071818]">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-7 h-7 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-teal-700 text-xs">Loading map...</p>
                </div>
              </div>
            }>
              <ShipmentMap shipment={shipment} />
            </Suspense>
          </MapErrorBoundary>

          {/* Gradient fade at bottom */}
          <div className="absolute bottom-0 inset-x-0 h-10 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #0d2020)' }} />
        </div>

        <div className="px-6 pb-6 space-y-5 mt-2">
          {/* Route header */}
          <div className="flex items-center gap-2 text-sm">
            <span className="flex items-center gap-1.5 font-semibold text-white">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block flex-shrink-0" />
              {shipment.origin?.city}
              {shipment.origin?.country ? `, ${shipment.origin.country}` : ''}
            </span>
            <i className="ri-arrow-right-line text-teal-700 text-xs flex-shrink-0" />
            <span className="flex items-center gap-1.5 font-semibold text-white">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block flex-shrink-0" />
              {shipment.destination?.city}
              {shipment.destination?.country ? `, ${shipment.destination.country}` : ''}
            </span>
          </div>

          {/* Current location */}
          {shipment.currentLocation?.city && (
            <div className="flex items-center gap-2 bg-teal-900/20 border border-teal-800/30 rounded-xl px-4 py-3">
              <i className="ri-map-pin-2-fill text-teal-400 text-sm animate-pulse" />
              <div>
                <div className="text-teal-500 text-xs">Currently at</div>
                <div className="text-white text-sm font-semibold">{shipment.currentLocation.city}</div>
              </div>
            </div>
          )}

          {/* Note */}
          {shipment.note && (
            <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/30 rounded-xl px-4 py-3">
              <i className="ri-information-line text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-200 text-xs leading-relaxed">{shipment.note}</p>
            </div>
          )}

          {/* Reason/message */}
          {shipment.reason && (
            <div className="bg-[#0b1e1e] border border-teal-800/30 rounded-xl px-4 py-3">
              <div className="text-teal-500 text-xs font-semibold uppercase tracking-wide mb-1.5">Customer Message</div>
              <p className="text-white/80 text-xs leading-relaxed">&ldquo;{shipment.reason}&rdquo;</p>
            </div>
          )}

          {/* Sender & Receiver */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0b1e1e] border border-white/5 rounded-xl px-4 py-4">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--primary) 20%, transparent)' }}>
                  <i className="ri-user-line text-xs" style={{ color: 'var(--primary)' }} />
                </div>
                <span className="text-teal-500 text-xs font-semibold uppercase tracking-wide">From</span>
              </div>
              <div className="text-white text-sm font-semibold">{shipment.sender?.name}</div>
              <div className="text-teal-600 text-xs mt-0.5">{shipment.sender?.city}</div>
              {shipment.sender?.country && <div className="text-teal-700 text-xs">{shipment.sender.country}</div>}
              {shipment.sender?.phone && <div className="text-teal-700 text-xs mt-1">{shipment.sender.phone}</div>}
            </div>
            <div className="bg-[#0b1e1e] border border-white/5 rounded-xl px-4 py-4">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-5 h-5 rounded-md bg-emerald-900/40 flex items-center justify-center">
                  <i className="ri-map-pin-user-line text-xs text-emerald-400" />
                </div>
                <span className="text-teal-500 text-xs font-semibold uppercase tracking-wide">To</span>
              </div>
              <div className="text-white text-sm font-semibold">{shipment.receiver?.name}</div>
              <div className="text-teal-600 text-xs mt-0.5">{shipment.receiver?.city}</div>
              {shipment.receiver?.country && <div className="text-teal-700 text-xs">{shipment.receiver.country}</div>}
              {shipment.receiver?.email && (
                <div className="text-teal-500 text-xs mt-1 flex items-center gap-1 truncate">
                  <i className="ri-mail-line text-xs flex-shrink-0" />
                  <span className="truncate">{shipment.receiver.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Package details */}
          <div className="bg-[#0b1e1e] border border-white/5 rounded-xl px-4 py-4">
            <div className="text-teal-500 text-xs font-semibold uppercase tracking-wide mb-3">Package Details</div>
            <div className="grid grid-cols-2 gap-y-2.5 gap-x-4">
              {[
                { label: 'Contents', value: shipment.description },
                { label: 'Service', value: shipment.service },
                { label: 'Weight', value: `${shipment.weight} kg` },
                { label: 'Dimensions', value: shipment.dimensions || '—' },
                { label: 'Declared Value', value: shipment.value ? `$${shipment.value.toLocaleString()}` : '—' },
                { label: 'Carrier', value: shipment.carrier || 'SwiftTrack' },
                { label: 'Est. Delivery', value: shipment.estimatedDelivery || '—' },
                { label: 'Payment', value: shipment.paymentMethod || '—' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div className="text-teal-700 text-xs">{label}</div>
                  <div className="text-white text-xs font-semibold mt-0.5 break-words">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-[#0b1e1e] border border-white/5 rounded-xl px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="text-teal-500 text-xs font-semibold uppercase tracking-wide">Tracking History</div>
              <span className="flex items-center gap-1 text-xs text-teal-600">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                {shipment.timeline?.length || 0} events
              </span>
            </div>
            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {[...(shipment.timeline || [])].reverse().map((evt, i) => {
                const evtCfg = STATUS_CONFIG[evt.status];
                return (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border ${evtCfg.bg} ${evtCfg.color}`}>
                        <i className={`${evtCfg.icon} text-xs`} />
                      </div>
                      {i < (shipment.timeline?.length || 0) - 1 && (
                        <div className="w-px flex-1 bg-teal-900/40 mt-1 min-h-4" />
                      )}
                    </div>
                    <div className="pb-3 min-w-0">
                      <div className={`font-semibold text-xs ${evtCfg.color}`}>{evtCfg.label}</div>
                      <div className="text-white/80 text-xs mt-0.5">{evt.description}</div>
                      <div className="text-teal-700 text-xs mt-0.5">{evt.location} · {evt.timestamp}</div>
                    </div>
                  </div>
                );
              })}
              {(!shipment.timeline || shipment.timeline.length === 0) && (
                <p className="text-teal-800 text-xs text-center py-4">No timeline events yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex gap-2 px-6 py-4 border-t border-white/5 flex-shrink-0">
        <button
          onClick={() => onStatusUpdate(shipment)}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white hover:opacity-90 cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap"
          style={{ background: 'var(--primary)' }}
        >
          <i className="ri-refresh-line" />Update Status
        </button>
        <button
          onClick={() => onEdit(shipment)}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-teal-300 border border-teal-800/50 hover:bg-teal-900/20 cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap"
        >
          <i className="ri-file-edit-line" />Edit Details
        </button>
        <Link
          to={`/track/${shipment.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-teal-400 border border-teal-800/50 hover:bg-teal-900/20 cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap"
        >
          <i className="ri-external-link-line" />Track Page
        </Link>
      </div>
    </div>
  );
}
